import React from 'react';
import { ActivityAction, EntityType } from '../../ActivityMenu/types';
import {
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
} from '../../ActivityMenu/styles/constants';
import { FilterPills as ReusableFilterPills, FilterOption } from '@/components/common/FilterPills';
import { PillBar, Pill } from '../../ActivityMenu/styles/styled';

export interface UserFilterOption {
  id: string;
  displayName: string;
}

// Support both old and new interface for backward compatibility
interface FilterPillsProps {
  // New interface with both filters
  currentActionFilter?: ActivityAction | 'all';
  onActionFilterChange?: (filter: ActivityAction | 'all') => void;
  currentTypeFilter?: EntityType | 'all';
  onTypeFilterChange?: (filter: EntityType | 'all') => void;
  /** User filter: 'all' or user id; options show display names */
  currentUserFilter?: string | 'all';
  onUserFilterChange?: (filter: string | 'all') => void;
  userOptions?: UserFilterOption[];
  // Old interface (for backward compatibility)
  currentFilter?: ActivityAction | 'all';
  onFilterChange?: (filter: ActivityAction | 'all') => void;
}

export const FilterPills: React.FC<FilterPillsProps> = ({
  currentActionFilter,
  onActionFilterChange,
  currentTypeFilter,
  onTypeFilterChange,
  currentUserFilter,
  onUserFilterChange,
  userOptions = [],
  currentFilter,
  onFilterChange,
}) => {
  // Check if using new interface (both filters) or old interface (single filter)
  const isNewInterface =
    currentActionFilter !== undefined && currentTypeFilter !== undefined;
  const isOldInterface =
    currentFilter !== undefined && onFilterChange !== undefined;

  // If using new interface with both filters
  if (isNewInterface && onActionFilterChange && onTypeFilterChange) {
    const typeOptions: FilterOption[] = ENTITY_TYPES.map((type) => ({
      id: type,
      label: type === 'all' ? 'All Types' : type,
    }));

    const actionOptions: FilterOption[] = ACTIVITY_ACTIONS.map((action) => ({
      id: action,
      label: action === 'all' ? 'All Actions' : action,
    }));

    const userFilterOptions: FilterOption[] = [
      { id: 'all', label: 'All Users' },
      ...userOptions.map((u) => ({ id: u.id, label: u.displayName })),
    ];

    return (
      <>
        {userOptions.length > 0 && onUserFilterChange && (
          <>
            <ReusableFilterPills
              label="Filter by User"
              options={userFilterOptions}
              currentFilter={currentUserFilter ?? 'all'}
              onFilterChange={(filter) =>
                onUserFilterChange(filter as string | 'all')
              }
            />
          </>
        )}
        <ReusableFilterPills
          label="Filter by Type"
          options={typeOptions}
          currentFilter={currentTypeFilter || 'all'}
          onFilterChange={(filter) => onTypeFilterChange(filter as EntityType | 'all')}
        />
        <ReusableFilterPills
          label="Filter by Action"
          options={actionOptions}
          currentFilter={currentActionFilter || 'all'}
          onFilterChange={(filter) => onActionFilterChange(filter as ActivityAction | 'all')}
        />
      </>
    );
  }

  // If using old interface (backward compatibility)
  if (isOldInterface && onFilterChange) {
    return (
      <PillBar>
        {ACTIVITY_ACTIONS.map((action) => (
          <Pill
            key={action}
            $active={currentFilter === action}
            onClick={() => onFilterChange(action)}
          >
            {action === 'all' ? 'All' : action}
          </Pill>
        ))}
      </PillBar>
    );
  }

  return null;
};
